import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useWizard } from "@customer-experience/cama-react";
import { useForm } from "react-hook-form";
import { makeStyles } from "@material-ui/core/styles";
import { Box } from "@material-ui/core";
import Loading from "../components/Layout/Loading";
import Step from "../components/Steps";
import Form from "../components/Steps/Form";
import Buttons from "../components/Steps/Buttons";
import Typography from "@material-ui/core/Typography";
import { getCurrentStepNumber } from "../utils/currentStepNumber";
import { stepButtonsVisibility } from "../utils";
import useFormSubmission from "../hooks/useFormSubmission";
import useNavigationSync from "../hooks/useNavigationSync";

import { ReactComponent as LogoEncrypted } from "../assets/images/logo-encrypted.svg";

const machineConfigId = "mobility-right-new";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
  },
  progress: {
    color: theme.palette.text.primary,
    opacity: "0.7",
    [theme.breakpoints.up("md")]: {
      display: "flex",
      justifyContent: "space-between",
      textAlign: "unset",
    },
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(2),
    },
  },
  stepNumber: {
    [theme.breakpoints.down("sm")]: {
      position: "absolute",
      right: 0,
      paddingRight: theme.spacing(2),
    },
  }
}));

const Steps = () => {
  const history = useHistory();
  const classes = useStyles();

  const form = useForm();
  const wizard = useWizard({
    machineConfigId,
  });

  const {
    questions,
    machineAnswers,
    isLoading,
    reset,
    stepKey,
    submit,
  } = wizard;

  const { formState, reset: formReset } = form;
  const { isValidating } = formState;

  const { handleSubmit, isSubmitting } = useFormSubmission({
    stepKey,
    questions,
    submit,
    formReset,
    machineAnswers,
  })

  useNavigationSync({ handleSubmit, wizard, form });

  const currentStepNumber = getCurrentStepNumber({ key: stepKey });

  const {
    shouldDisplayButtons,
    shouldDisplayBack,
    shouldDisplayNext,
  } = stepButtonsVisibility({ questions, stepKey });

  const isContactDetails = stepKey === "CONTACT_DETAILS";
  const isFinal = stepKey === "RESULT_PARKING";

  // Always start the claim check from the beginning
  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldReset = history.location.pathname === "/reset";
  useEffect(() => {
    if (shouldReset) {
      reset();
      history.push("/");
    }
  }, [reset, history, shouldReset]);

  const displayLoading = (isLoading || isSubmitting) && !isValidating;
  if (displayLoading) {
    return (
      <Loading
        isLoading={isLoading || isSubmitting}
        isLongWait={isContactDetails && isSubmitting}
      />
    );
  }

  return (
    <div className={classes.root}>
      <Form form={form} onSubmit={handleSubmit}>
        <Step wizard={wizard} onSubmit={handleSubmit} />

        {shouldDisplayButtons && (
          <Buttons
            showBackButton={shouldDisplayBack}
            showNextButton={shouldDisplayNext}
            goBack={history.goBack}
            backButtonText={isFinal ? "Zurück zur Übersicht" : "Zurück"}
            submitBtnText={isContactDetails ? "Auswertung erhalten" : "Weiter"}
            isDisabled={isLoading || isSubmitting}
          />
        )}
      </Form>

      <Box className={classes.progress} my={2}>
        <LogoEncrypted title="256bit AES Verschlüsselt" />

        {currentStepNumber && (
          <Typography variant="body1" component="span" className={classes.stepNumber}>
            <strong>{currentStepNumber}</strong> von <strong>4</strong>
          </Typography>
        )}
      </Box>
    </div>
  );
};

export default Steps;
